import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSaveUserDetailsMutation } from '../queries/saveUserDetails.mutation.generated';
import { type GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];

export interface UseUserEditFormReturn {
  editing: boolean;
  setEditing: (v: boolean) => void;
  displayName: string;
  setDisplayName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  saving: boolean;
  /** True while editing and a field differs from what it held when the edit opened. */
  hasChanges: boolean;
}

export function useUserEditForm(user: UserDetail, refetch: () => void): UseUserEditFormReturn {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user.display_name ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [phone, setPhone] = useState(user.phone || '');
  // What the fields held when the edit opened. Compared against this rather than
  // `user`, because the fields are seeded once and can disagree with `user`.
  const [seed, setSeed] = useState({ displayName, email, phone });

  const startOrStopEditing = useCallback(
    (v: boolean) => {
      if (v) setSeed({ displayName, email, phone });
      setEditing(v);
    },
    [displayName, email, phone],
  );

  const hasChanges =
    editing && (displayName !== seed.displayName || email !== seed.email || phone !== seed.phone);

  const [updateUser, { loading: saving }] = useSaveUserDetailsMutation();

  const handleSave = useCallback(async () => {
    try {
      await updateUser({
        variables: {
          userId: user.id,
          userObj: {
            display_name: displayName,
            email,
            phone: phone || null,
          },
        },
        refetchQueries: ['GetUserDetails'],
      });
      toast.success('User updated successfully');
      setEditing(false);
      refetch();
    } catch (err: any) {
      toast.error(`Failed to update user: ${err.message}`);
    }
  }, [user.id, displayName, email, phone, updateUser, refetch]);

  const handleCancel = useCallback(() => {
    setDisplayName(user.display_name ?? '');
    setEmail(user.email ?? '');
    setPhone(user.phone || '');
    setEditing(false);
  }, [user.display_name, user.email, user.phone]);

  return {
    editing,
    setEditing: startOrStopEditing,
    displayName,
    setDisplayName,
    email,
    setEmail,
    phone,
    setPhone,
    handleSave,
    handleCancel,
    saving,
    hasChanges,
  };
}
